import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface FollowerProfile {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

/**
 * Fetches follower profiles for a collection.
 * Returns a list of profiles for users who follow the collection.
 */
export function useCollectionFollowerProfiles(collectionId?: string) {
  const { data: followers = [], isLoading } = useQuery({
    queryKey: ['collection-followers', collectionId],
    queryFn: async () => {
      if (!collectionId) return [];

      // Get all follower IDs for this collection
      const { data: follows, error: followsError } = await supabase
        .from('collection_follows')
        .select('follower_id')
        .eq('collection_id', collectionId);

      if (followsError) throw followsError;
      if (!follows || follows.length === 0) return [];

      // Fetch profiles for these followers
      const followerIds = follows.map(f => f.follower_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, display_name, avatar_url')
        .in('user_id', followerIds);

      if (profilesError) throw profilesError;
      return (profiles || []) as FollowerProfile[];
    },
    enabled: !!collectionId,
    staleTime: 1000 * 60, // 1 minute
  });

  return { followers, isLoading };
}

/**
 * Fetches follower profiles for multiple collections at once.
 * Useful for grid views where we need to show followers for many collections.
 */
export function useMultipleCollectionFollowerProfiles(collectionIds: string[]) {
  const { data: followersMap = {}, isLoading } = useQuery({
    queryKey: ['collection-followers-batch', collectionIds.sort().join(',')],
    queryFn: async () => {
      if (collectionIds.length === 0) return {};

      // Get all follows for these collections
      const { data: follows, error: followsError } = await supabase
        .from('collection_follows')
        .select('follower_id, collection_id')
        .in('collection_id', collectionIds);

      if (followsError) throw followsError;
      if (!follows || follows.length === 0) return {};

      // Get unique follower IDs
      const uniqueFollowerIds = [...new Set(follows.map(f => f.follower_id))];

      // Fetch all profiles at once
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, display_name, avatar_url')
        .in('user_id', uniqueFollowerIds);

      if (profilesError) throw profilesError;

      // Create a map of user_id -> profile
      const profileMap = new Map<string, FollowerProfile>();
      (profiles || []).forEach(p => profileMap.set(p.user_id, p));

      // Group followers by collection
      const result: Record<string, FollowerProfile[]> = {};
      follows.forEach(f => {
        if (!result[f.collection_id]) {
          result[f.collection_id] = [];
        }
        const profile = profileMap.get(f.follower_id);
        if (profile) {
          result[f.collection_id].push(profile);
        }
      });

      return result;
    },
    enabled: collectionIds.length > 0,
    staleTime: 1000 * 60, // 1 minute
  });

  return { followersMap, isLoading };
}
